import React from "react";

export type ButtonVariant =
  | "primary"
  | "danger"
  | "success"
  | "secondary"
  | "outline"
  | "permissions";

export interface ButtonProps {
  label: string;
  onClick?: () => void;
  type?: "button" | "submit" | "reset";
  variant?: ButtonVariant;
  size?: "sm" | "md" | "lg";
  paddingX?: string;
  paddingY?: string;
  rounded?: string;
  disabled?: boolean;
  loading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: "left" | "right";
  className?: string;
  style?: React.CSSProperties;
}

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary:
    "bg-blue-base hover:bg-blue-base-hover active:bg-blue-base-active text-white",
  danger:
    "bg-red-base hover:bg-red-base-hover active:bg-red-base-active text-white",
  success: "bg-green-600 hover:bg-green-700 active:bg-green-800 text-white",
  secondary: "bg-gray-500 hover:bg-gray-600 active:bg-gray-700 text-white",
  outline:
    "bg-transparent hover:bg-blue-50 active:bg-blue-100 text-blue-base border border-blue-base",
  permissions:
    "bg-blue-100 hover:bg-blue-50 active:bg-blue-100 text-blue-base border border-blue-base ",
};

export const CustomButton: React.FC<ButtonProps> = ({
  label,
  onClick,
  type = "button",
  variant = "primary",
  size = "md",
  paddingX,
  paddingY,
  rounded = "rounded-inputs",
  disabled = false,
  loading = false,
  icon,
  iconPosition = "left",
  className = "",
  style = {},
}) => {
  const sizeClasses = {
    sm: "px-2 py-1 text-sm",
    md: "px-4 py-1 text-base",
    lg: "px-6 py-1 text-lg",
  };

  const paddingClasses =
    paddingX || paddingY
      ? `${paddingX ?? ""} ${paddingY ?? ""}`
      : sizeClasses[size];

  const variantClasses = VARIANT_CLASSES[variant];

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={`
        font-medium transition-all duration-200 max-h-[30px] max-w-fit
    flex items-center justify-center text-center
        ${variantClasses}
        ${paddingClasses}
        ${rounded}
        ${disabled || loading ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
        ${className}
      `}
      style={style}
    >
      <div className="flex items-center justify-center gap-2">
        {loading && (
          <svg
            className="animate-spin h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}

        {!loading && icon && iconPosition === "left" && (
          <span className="shrink-0">{icon}</span>
        )}

        <span>{loading ? "Processando..." : label}</span>

        {!loading && icon && iconPosition === "right" && (
          <span className="shrink-0">{icon}</span>
        )}
      </div>
    </button>
  );
};

export default CustomButton;
